<template>
  <TwoFactor v-if="twoFactor" :email="loginData.email" :password="loginData.password" />
  <form v-else class="flex flex-col w-full" @submit.prevent="login">
    <h2 class="mb-2">
      {{ $t('login.title') }}
    </h2>
    <div class="mt-6 flex flex-col gap-y-2 w-full">
      <InputField
        v-model="loginData.email"
        :error="errors?.email"
        type="email"
        :label="$t('login.emailLabel')"
        class="my-2 text-left"
      />
      <InputField
        v-model="loginData.password"
        :error="errors?.password"
        type="password"
        class="mt-2 mb-2 text-left"
        :label="$t('login.passwordLabel')"
      />
    </div>
    <div class="flex justify-end text-pre mb-4 mt-2">
      <router-link :to="{ name: 'forgotPassword' }">
        {{ $t('login.forgotPassword') }}
      </router-link>
    </div>
    <custom-button
      type="submit"
      variant="primary"
      class="mt-3"
      :busy="loading"
    >
      {{ $t('login.buttonText') }}
    </custom-button>
  </form>
</template>

<script lang="ts" setup>
import { reactive, ref } from 'vue';
import { FetchError, useFetchAction } from '@hooks/fetch';
import { useRouter } from 'vue-router';
import { useValidation } from '@hooks/form';
import { object, string } from 'yup';
import { useI18n } from 'vue-i18n';
import { useLocalStorage } from '@vueuse/core';
import { tableFieldEntriesKey } from '@hooks/table-configuration';
import { enumOptionsCacheKey } from '@utils/table/hooks/provider';
import InputField from '@/components/utils/inputs/Input.vue';
import { setAuthToken } from '@/utils/tokenManager';
import { useStore } from '@/store';
import type {
  HydraCollection,
  TableFieldsEntry,
  User,
  WorkingTableFieldsEntry,
} from '@/entities/Entities';
import TwoFactor from './TwoFactor.vue';
import { useNotify } from '@/components/utils/notifications';

const twoFactor = ref(false);
const TWO_FACTOR_ERROR_CODE = 'two_factor_needed';

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const {
  data: loginData,
  errors,
  validatedFn,
} = useValidation(
  object({
    email: string().email(t('login.emailValid')),
    password: string().required(t('login.password')),
  }),
  reactive({
    email: '',
    password: '',
  }),
);

const [query, { loading }] = useFetchAction();

const notify = useNotify();

const login = validatedFn(async () => {
  let loggedInUser: User | undefined;

  try {
    setAuthToken();
    const { token, user } = await query<{ token: string; user: User }>('/login', {
      method: 'POST',
      body: {
        email: loginData.value.email,
        password: loginData.value.password,
      },
    });

    if (token && user) {
      setAuthToken(token);
      store.commit('auth/SET_USER', user);
      loggedInUser = user;

      const tableFieldEntries = useLocalStorage<Record<string, WorkingTableFieldsEntry>>(
        tableFieldEntriesKey,
        {},
      );
      tableFieldEntries.value = {};
      const members =
        (await query<HydraCollection<TableFieldsEntry>>('/table_fields_entries', {}))?.[
          'hydra:member'
        ] ?? [];
      tableFieldEntries.value = members.reduce((acc, member) => {
        acc[member.pathKey] = {
          id: member['@id'],
          activeFields: member.activeFields,
        };
        return acc;
      }, {} as Record<string, WorkingTableFieldsEntry>);

      // clear enums from local storage to prevent stale data
      const enumOptions = useLocalStorage<Record<string, string[]>>(enumOptionsCacheKey, {});
      enumOptions.value = null;
    }
  } catch (e) {
    if (e instanceof FetchError) {
      const errorCode = await e.response.json();
      if (errorCode?.error?.key === TWO_FACTOR_ERROR_CODE) {
        twoFactor.value = true;
      } else {
        notify?.({
          variant: 'error',
          title: t('login.errorNotification'),
        });
      }
    } else {
      notify?.({
        variant: 'error',
        title: t('serverError'),
      });
    }
    return;
  }

  if (loggedInUser) {
    if (router.currentRoute.value.redirectedFrom) {
      await router.push(router.currentRoute.value.redirectedFrom);
    } else {
      await router.push({ name: 'home', params: { locale: loggedInUser.language } });
    }
  }
});
</script>

<style lang="scss" scoped></style>
